<template>
    <div class="lms__superadmin_workspaces_edit_customfields">
        <template v-if="isAddMode">
            <component-empty-state
                :item="'Key'"
                :title="'Custom fields for the workspace can be added when workspace is created. Save workspace data first.'"
                :size="'full'"
                :submit-button-active="false"
            />
        </template>
        <template v-else>
            <component-induction-message
                :class="'lms__superadmin_workspaces_edit_customfields_induction'"
                :title="`User fields`"
                :content="'Create custom fields for your workspaces user profiles.\n'+
                'Make a field mandatory if you require your users to enter certain information.\n'+
                '\n'+
                'You can create a maximum of 10 custom user fields.'"
                :theme="'primary'"
                :route-name="isAddMode ? 'lms-superadmin-workspace-add-custom-fields' : 'lms-superadmin-workspace-edit-custom-fields'"
            ></component-induction-message>
            <view-actions
                :title="'User fields'"
                :cancel-button-active="false"
                :submit-button-text="'Add field'"
                :submit-button-active="true"
                :raw="true"
                @emit-submit="handleAddField"
            />
            <component-list
                ref="customfieldslist"
                :class="['lms__superadmin_workspaces_edit_customfields_list']"
                :component-table="LmsSuperadminWorkspaceEditCustomFieldsTable"
                :component-table-name="'fields'"
                :get-items-getter="'getLmsSuperadminWorkspaceCustomFieldsData'"
                :get-items-dispatch="workspaceId ? 'lmsSuperadminCustomFieldsGet' : ''"
                :get-items-dispatch-data="workspaceId"
                :item-name="['field', 'fields']"
                :filters-active="false"
                :filter-overlay="false"
                :empty-state-title="'There are no custom fields added to the workspace yet.'"
                :get-items-dispatch-trigger-name="'get-custom-fields'"
                @emit-edit="handleEditField"
                @emit-remove="handleRemoveField"
            ></component-list>
            <pop-notify
                ref="notifyaddeditfield"
                :class="'pop__notify--edit lms__superadmin_workspaces_edit_customfields_keyform'"
                :title-text="popNotifyProcess === 'add' ? 'Add user field' : 'Edit user field'"
                :content-text="''"
                :cancel-button-active="false"
                :submit-button-active="false"
                :size="'medium'"
            >
                <form-text
                    :class="'lms__superadmin_workspaces_edit_customfields_keyform_name'"
                    :name="'lms-superadmin-workspace-edit-customfields-name'"
                    :label="'Field name'"
                    :required="true"
                    :v-validate="{ required: true }"
                    :data-vv-as="'key name'"
                    v-model="fieldName"
                ></form-text>
                <form-radio-horizontal
                    :label="'Field type'"
                    :disabled="popNotifyProcess === 'edit'"
                    :items="[
                        {
                            name: 'Date',
                            value: 'date'
                        },
                        {
                            name: 'Email',
                            value: 'email'
                        },
                        {
                            name: 'Number',
                            value: 'numeric'
                        },
                        {
                            name: 'Free text',
                            value: 'text'
                        },
                        {
                            name: 'A to Z',
                            value: 'alpha'
                        }
                    ]"
                    :name="'lms-superadmin-workspace-edit-customfields-type'"
                    :required="true"
                    :v-validate="{ required: true }"
                    :data-vv-as="'status'"
                    :force-vertical="true"
                    v-model="fieldType"
                ></form-radio-horizontal>
                <form-number
                    v-if="fieldType === 'text' || fieldType === 'numeric' || fieldType === 'alpha'"
                    :class="'lms__superadmin_workspaces_edit_customfields_keyform_customvalidation_minlimit'"
                    :name="'lms-superadmin-workspace-edit-customfields-custom-validation-min-limit'"
                    :label="'Min. character limit'"
                    :v-validate="{ max_value: fieldCustomValidationMaxLength > 0 ? fieldCustomValidationMaxLength : false }"
                    :data-vv-as="'min. character limit'"
                    v-model="fieldCustomValidationMinLength"
                ></form-number>
                <form-number
                    v-if="fieldType === 'text' || fieldType === 'numeric' || fieldType === 'alpha'"
                    :class="'lms__superadmin_workspaces_edit_customfields_keyform_customvalidation_maxlimit'"
                    :name="'lms-superadmax-workspace-edit-customfields-custom-validation-max-limit'"
                    :label="'Max. character limit'"
                    :v-validate="{ min_value: fieldCustomValidationMinLength > 0 && fieldCustomValidationMaxLength > 0 ? fieldCustomValidationMinLength : false }"
                    :data-vv-as="'max. character limit'"
                    v-model="fieldCustomValidationMaxLength"
                ></form-number>
                <component-wrapper
                    :class="'lms__superadmin_workspaces_edit_customfields_keyform_customvalidation_wrapper'"
                    v-if="fieldType === 'select' || fieldType === 'multiple'"
                    :overlay="true"
                >
                    <form-textarea
                        :class="'lms__superadmin_workspaces_edit_customfields_keyform_customvalidation_wrapper_set'"
                        :name="'lms-superadmax-workspace-edit-customfields-max-limit'"
                        :label="'Options'"
                        :v-validate="{ required: true }"
                        :data-vv-as="'options'"
                        v-model="fieldCustomValidationSet"
                    >Add a new option for each line
                    </form-textarea>
                </component-wrapper>
                <form-text
                    :class="'lms__superadmin_workspaces_edit_customfields_keyform_help'"
                    :name="'lms-superadmin-workspace-edit-customfields-help'"
                    :label="'Help text'"
                    v-model="fieldHelp"
                >This is how help text displays underneath fields when inviting a user.
                </form-text>
                <form-checkbox
                    :class="'lms__superadmin_workspaces_edit_customfields_keyform_required'"
                    :name="'lms-superadmin-workspace-edit-customfields-required'"
                    :label="'User must provide information'"
                    v-model="fieldRequired"
                >Required field when a user is being invited to the workspace.
                </form-checkbox>
                <div class="lms__superadmin_workspaces_edit_customfields_keyform_actions">
                    <div class="columns is-variable">
                        <div class="column"></div>
                        <div class="column is-narrow">
                            <component-button
                                :name="'lms-superadmin-workspace-edit-customfields-cancel'"
                                :theme="'default'"
                                @click="closeEditFieldFormNotify"
                            >Cancel
                            </component-button>
                        </div>
                        <div class="column is-narrow">
                            <component-button
                                :name="'lms-superadmin-workspace-edit-customfields-submit'"
                                v-on="popNotifyProcess === 'add' ? { 'click': performCreateField } : { 'click': handleOverrideField }"
                            >{{ popNotifyProcess === 'add' ? 'Add field' : 'Save field' }}
                            </component-button>
                        </div>
                    </div>
                </div>
            </pop-notify>
            <pop-notify
                :class="'pop__notify--remove'"
                ref="notifyremovecustomfield"
                :title-text="'Remove user field'"
                :content-text="'Are you sure want to remove this user field?'"
                :cancel-button-text="'Dismiss'"
                :submit-button-text="'Confirm'"
                :submit-button-danger="true"
                :submit-button-animate-loading="true"
                :submit-button-animate-loading-name="'remove-custom-field'"
                :prevent-close="true"
                @emit-submit="performRemoveField"
            />
            <pop-notify
                :class="'pop__notify--override'"
                ref="notifyoverridecustomfield"
                :title-text="'Overwrite user field?'"
                :content-text="'Overwriting the user field may mean your learners may need to update this field. Would you like to save the changes?'"
                :cancel-button-text="'Cancel'"
                :submit-button-text="'Confirm'"
                :submit-button-danger="true"
                :submit-button-animate-loading="true"
                :submit-button-animate-loading-name="'override-custom-field'"
                :prevent-close="true"
                @emit-submit="performUpdateField"
            />
        </template>
    </div>
</template>

<script>
import { performSubmitValidate, validateBeforeRouteLeave, watchContentChangeEmit } from '@/helpers/mixins'
import { User } from '@/User'
import ComponentButton from '@/components/default/shared/ComponentButton'
import FormText from '@/components/default/forms/FormText'
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import PopNotify from '@/components/default/shared/PopNotify'
import ComponentList from '@/components/default/shared/ComponentList'
import FormNumber from '@/components/default/forms/FormNumber'
import LmsSuperadminWorkspaceEditCustomFieldsTable from '@/components/lms/superadmin/workspace/edit/LmsSuperadminWorkspaceEditCustomFieldsTable'
import ComponentEmptyState from '@/components/default/shared/ComponentEmptyState'
import FormRadioHorizontal from '@/components/default/forms/FormRadioHorizontal'
import ViewActions from '@/components/default/shared/ViewActions'
import ComponentInductionMessage from '@/components/default/shared/ComponentInductionMessage'
import FormTextarea from '@/components/default/forms/FormTextarea'
import ComponentWrapper from '@/components/default/shared/ComponentWrapper'

export default {
  name: 'lms-superadmin-workspace-edit-customfields',
  components: {
    ComponentEmptyState,
    ComponentWrapper,
    FormTextarea,
    ComponentInductionMessage,
    ViewActions,
    FormRadioHorizontal,
    FormNumber,
    ComponentList,
    PopNotify,
    FormCheckbox,
    FormText,
    ComponentButton
  },
  mixins: [validateBeforeRouteLeave, performSubmitValidate, watchContentChangeEmit],
  data () {
    return {
      user: new User(),
      popNotifyProcess: 'add',
      LmsSuperadminWorkspaceEditCustomFieldsTable
    }
  },
  computed: {
    workspaceId () {
      return this.$route.params.id
    },
    fieldName: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceCustomFieldName
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkspaceCustomFieldSetName', value)
      }
    },
    fieldType: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceCustomFieldType
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkspaceCustomFieldSetFieldType', value)
      }
    },
    fieldCustomValidationMinLength: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceCustomFieldCustomValidationMinLength
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkspaceCustomFieldSetCustomValidationMinLength', value)
      }
    },
    fieldCustomValidationMaxLength: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceCustomFieldCustomValidationMaxLength
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkspaceCustomFieldSetCustomValidationMaxLength', value)
      }
    },
    fieldCustomValidationSet: {
      get () {
        const rawData = this.$store.getters.getLmsSuperadminWorkspaceCustomFieldCustomValidationSet
        return rawData ? rawData.join('\n') : rawData
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkspaceCustomFieldSetCustomValidationSet', value.split('\n'))
      }
    },
    fieldHelp: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceCustomFieldHelp
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkspaceCustomFieldSetHelp', value)
      }
    },
    fieldRequired: {
      get () {
        return this.$store.getters.getLmsSuperadminWorkspaceCustomFieldRequired
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminWorkspaceCustomFieldSetRequired', value)
      }
    },
    isAddMode () {
      return this.$route.name.includes('workspace-add')
    },
    isEditMode () {
      return this.$route.name.includes('workspace-edit')
    }
  },
  methods: {
    handleAddField () {
      this.popNotifyProcess = 'add'
      this.openEditFieldFormNotify()
    },
    handleEditField (data) {
      this.popNotifyProcess = 'edit'
      this.openEditFieldFormNotify()
      this.$store.dispatch('lmsSuperadminWorkspaceCustomFieldGet', data)
    },
    handleOverrideField (data) {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.openOverrideFieldFormNotify()
        }
      })
    },
    handleRemoveField (data) {
      this.$refs.notifyremovecustomfield.setStoreValue(data)
      this.openRemoveFieldFormNotify()
    },
    performCreateField () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$store.dispatch('lmsSuperadminWorkspaceCustomFieldCreate', this.workspaceId).then(() => {
            this.closeEditFieldFormNotify()
            this.$refs.customfieldslist.getItems()
          }).finally(() => {
            this.$eventBus.$emit('stop-custom-field-submit')
          })
        } else {
          this.$eventBus.$emit('stop-custom-field-submit')
        }
      })
    },
    performUpdateField () {
      this.$store.dispatch('lmsSuperadminWorkspaceCustomFieldUpdate').then(() => {
        this.closeEditFieldFormNotify()
        this.closeOverrideFieldFormNotify()
        this.$refs.customfieldslist.getItems()
      }).finally(() => {
        this.$eventBus.$emit('stop-override-custom-field')
      })
    },
    performRemoveField () {
      const fieldId = this.$refs.notifyremovecustomfield.storeValue
      this.$store.dispatch('lmsSuperadminWorkspaceCustomFieldRemove', fieldId).then(() => {
        this.closeRemoveFieldFormNotify()
        this.$refs.customfieldslist.getItems()
      }).finally(() => {
        this.$eventBus.$emit('stop-remove-custom-field')
      })
    },
    openOverrideFieldFormNotify () {
      this.$refs.notifyoverridecustomfield.openNotify()
    },
    closeOverrideFieldFormNotify () {
      this.$refs.notifyoverridecustomfield.closeNotify()
    },
    openRemoveFieldFormNotify () {
      this.$refs.notifyremovecustomfield.openNotify()
    },
    closeRemoveFieldFormNotify () {
      this.$refs.notifyremovecustomfield.closeNotify()
    },
    openEditFieldFormNotify () {
      this.$store.dispatch('lmsSuperadminWorkspaceCustomFieldClear')
      this.$refs.notifyaddeditfield.openNotify()
    },
    closeEditFieldFormNotify () {
      this.$refs.notifyaddeditfield.closeNotify()
    }
  },
  provide () {
    return {
      $validator: this.$validator
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/lms/superadmin/workspaces/_lmssuperadminworkspacesaddeditcustomfields.scss";
</style>
